import React, { useState } from 'react';
import { CheckCheck, Pencil, Send, ShieldAlert, Trash2, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../ui/sheet';
import { Textarea } from '../ui/textarea';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '../ui/alert-dialog';
import { StatusBadge } from './StatusBadge';
import { api, aud, fmtDate, freqLabel } from './utils';
import { DepositCell } from './PlanTable';

const Stat = ({ label, value, tone = 'text-[#3A3530]', testId }) => (
  <div className="rounded-xl bg-[#F5F1E8] p-3">
    <p className="text-[10px] font-semibold uppercase tracking-wider text-[#9B9590]">{label}</p>
    <p className={`mt-1 font-semibold tabular-nums ${tone}`} data-testid={testId}>{value}</p>
  </div>
);

const Payments = ({ plan }) => (
  <div data-testid="drawer-payment-history">
    <h4 className="mb-2 text-sm font-semibold text-[#3A3530]">Payment history (Halaxy)</h4>
    {!plan.payments_loaded && <p className="text-xs text-[#9B9590]">Payment history loads on the next Halaxy refresh.</p>}
    {plan.payments_loaded && !plan.payments.length && <p className="text-xs text-[#9B9590]">No payments recorded on this invoice.</p>}
    <ul className="divide-y divide-[#F0ECE4] text-sm">
      {[...plan.payments].reverse().map((p, i) => (
        <li key={i} className="flex items-center justify-between py-2">
          <span className="text-[#6B6560]">{fmtDate(p.date)} · {p.method}</span>
          <span className={`tabular-nums font-medium ${p.type === 'Failed' ? 'text-[#C0392B]' : p.amount < 0 ? 'text-[#C17B6B]' : 'text-[#4A6B2E]'}`}>
            {p.type === 'Failed' ? 'Failed' : aud(p.amount)}{p.type === 'Funding' && <span className="ml-1 text-xs text-[#9B9590]">Medicare</span>}
            {p.date < plan.start_date && p.type !== 'Failed' && <span className="ml-1 text-xs text-[#9B9590]">(pre-plan)</span>}
          </span>
        </li>
      ))}
    </ul>
  </div>
);

const Notes = ({ plan, onSaved }) => {
  const [text, setText] = useState('');
  const add = async () => {
    try { onSaved(await api(`/${plan.id}/notes`, { method: 'POST', body: { text } })); setText(''); } catch (e) { toast.error(e.message); }
  };
  return (
    <div data-testid="drawer-notes">
      <h4 className="mb-2 text-sm font-semibold text-[#3A3530]">Follow-up log</h4>
      <div className="flex gap-2">
        <Textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder="Add a note…" data-testid="drawer-note-input" />
        <Button onClick={add} disabled={!text.trim()} className="self-end bg-[#8B9D83] text-white hover:bg-[#6B7D63]" data-testid="drawer-note-add-btn">Add</Button>
      </div>
      <ul className="mt-3 space-y-2">
        {[...plan.notes].reverse().map((n) => (
          <li key={n.id} className="rounded-lg border border-[#F0ECE4] p-2.5 text-sm">
            <p className="text-[#3A3530]">{n.text}</p>
            <p className="mt-0.5 text-[11px] text-[#9B9590]">{fmtDate(n.created_at, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
          </li>
        ))}
      </ul>
    </div>
  );
};

const Actions = ({ plan, onSaved, onEdit, onFollowUp, onDeleted }) => {
  const call = async (path, opts, msg) => {
    try { onSaved(await api(path, opts)); toast.success(msg); } catch (e) { toast.error(e.message); }
  };
  const del = async () => {
    try { await api(`/${plan.id}`, { method: 'DELETE' }); toast.success('Plan deleted'); onDeleted(plan); } catch (e) { toast.error(e.message); }
  };
  return (
    <div className="flex flex-wrap gap-2">
      <Button size="sm" onClick={() => onFollowUp(plan, 'Follow Up Sent')} className="bg-[#6B8FA3] text-white hover:bg-[#5A7E92]" data-testid="drawer-follow-up-btn"><Send className="mr-1.5 h-3.5 w-3.5" />Follow-up sent</Button>
      <Button size="sm" variant="outline" onClick={() => onFollowUp(plan, 'Escalated')} className="border-[#7A3B69]/40 text-[#7A3B69]" data-testid="drawer-escalate-btn"><ShieldAlert className="mr-1.5 h-3.5 w-3.5" />Escalate</Button>
      {plan.manual_status && (
        <Button size="sm" variant="outline" onClick={() => call(`/${plan.id}`, { method: 'PUT', body: { manual_status: 'Clear' } }, 'Status cleared')} data-testid="drawer-clear-status-btn"><Undo2 className="mr-1.5 h-3.5 w-3.5" />Clear status</Button>
      )}
      <Button size="sm" variant="outline" onClick={() => call(`/${plan.id}/checked`, { method: 'POST' }, 'Marked as checked')} data-testid="drawer-checked-btn"><CheckCheck className="mr-1.5 h-3.5 w-3.5" />Mark checked</Button>
      <Button size="sm" variant="outline" onClick={() => onEdit(plan)} data-testid="drawer-edit-btn"><Pencil className="mr-1.5 h-3.5 w-3.5" />Edit</Button>
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button size="sm" variant="ghost" className="text-[#C17B6B]" data-testid="drawer-delete-btn"><Trash2 className="h-3.5 w-3.5" /></Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this plan?</AlertDialogTitle>
            <AlertDialogDescription>Only delete plans added by mistake. Finished plans move to Completed automatically and keep their history.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="drawer-delete-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={del} className="bg-[#C0392B] hover:bg-[#A93226]" data-testid="drawer-delete-confirm-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export const PlanDrawer = ({ plan, onClose, ...handlers }) => (
  <Sheet open={!!plan} onOpenChange={(o) => !o && onClose()}>
    <SheetContent className="w-full overflow-y-auto sm:max-w-xl" data-testid="plan-drawer">
      {plan && (
        <div className="space-y-6">
          <SheetHeader>
            <div className="flex items-center gap-3">
              <SheetTitle className="font-heading text-2xl" data-testid="drawer-client-name">{plan.client_name}</SheetTitle>
              <StatusBadge status={plan.status} testId="drawer-status" />
            </div>
            <SheetDescription>
              {plan.assessment_type} · Invoice #{plan.invoice_id} · {aud(plan.instalment_amount)} {freqLabel(plan).toLowerCase()} from {fmtDate(plan.start_date)}
            </SheetDescription>
          </SheetHeader>
          {plan.halaxy_missing && <p className="rounded-lg bg-[#FFF5F3] p-3 text-sm text-[#C0392B]">This invoice was not found in Halaxy on the last refresh.</p>}
          <div className="grid grid-cols-3 gap-2">
            <Stat label="Invoiced" value={aud(plan.invoiced)} />
            <Stat label="Paid" value={aud(plan.paid)} tone="text-[#4A6B2E]" />
            <Stat label="Balance" value={aud(plan.balance)} testId="drawer-balance" />
            <Stat label="Original owing" value={aud(plan.original_balance)} />
            <Stat label="Next due" value={plan.next_due ? `${fmtDate(plan.next_due, { day: '2-digit', month: 'short' })} · ${aud(plan.next_due_amount)}` : '—'}
              tone={plan.needs_follow_up ? 'text-[#C0392B]' : 'text-[#3A3530]'} testId="drawer-next-due" />
            <Stat label="Halaxy plan" value={plan.halaxy_plan_setup ? 'Set up' : 'Not set up'} tone={plan.halaxy_plan_setup ? 'text-[#4A6B2E]' : 'text-[#C17B6B]'} />
            <Stat label="Last payment" testId="drawer-last-payment"
              value={plan.last_payment_date ? `${fmtDate(plan.last_payment_date, { day: '2-digit', month: 'short' })} · ${aud(plan.last_payment_amount)}` : '—'} />
            <Stat label="Expected payoff" value={fmtDate(plan.expected_payoff_date)} testId="drawer-payoff" />
            <div className="rounded-xl bg-[#F5F1E8] p-3" data-testid="drawer-deposit">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-[#9B9590]">Deposit</p>
              <div className="mt-1"><DepositCell p={plan} /></div>
            </div>
          </div>
          {plan.special_arrangements && <p className="rounded-xl border border-dashed border-[#D4A574] p-3 text-sm text-[#6B6560]"><b>Special arrangements:</b> {plan.special_arrangements}</p>}
          <Actions plan={plan} {...handlers} />
          <Payments plan={plan} />
          <Notes plan={plan} onSaved={handlers.onSaved} />
          <p className="text-[11px] text-[#9B9590]">Last checked {fmtDate(plan.last_checked_at)} · Halaxy data from {fmtDate(plan.halaxy_synced_at)}</p>
        </div>
      )}
    </SheetContent>
  </Sheet>
);
